import CacheService from './CacheService';
import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.costlycure.com';

class APIService {
  static navigationCallback = null;

  static setNavigationCallback(callback) {
    this.navigationCallback = callback;
  }

  /**
   * Validates that the API base URL uses HTTPS.
   * @throws {Error} If the URL doesn't use HTTPS protocol.
   */
  static _enforceHTTPS() {
    if (!API_BASE_URL.startsWith('https://')) {
      throw new Error('Insecure connection: API must use HTTPS protocol');
    }
  }

  /**
   * Implements exponential backoff delay for retries.
   * @param {number} attempt - Current retry attempt (0-based).
   * @returns {Promise<void>} Promise that resolves after the delay.
   */
  static async _exponentialBackoff(attempt) {
    // Calculate delay: 1s, 2s, 4s, 8s...
    const baseDelay = 1000;
    const delay = baseDelay * Math.pow(2, attempt);
    // Add random jitter to prevent thundering herd
    const jitter = Math.random() * 500;
    await new Promise(resolve => setTimeout(resolve, delay + jitter));
  }

  /**
   * Private helper to get common request headers.
   * @returns {HeadersInit} The common headers object.
   */
  static _getCommonHeaders() {
    const token = localStorage.getItem('authToken');
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const headers = {
      'Content-Type': 'application/json',
      'language-code': lang,
    };

    // Only add Authorization header if a token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // --- Utility method to handle the fetch request ---

  /**
 * Private helper to perform the fetch and handle the common logic.
 * @param {string} endpoint The API endpoint (e.g., '/llms').
 * @param {RequestInit} options The fetch options (method, body, custom headers, etc.).
 * @param {string} errorMessage The error message to throw on failure.
 * @param {number} retryCount Current retry attempt (for internal use).
 * @returns {Promise<any>} The parsed JSON response.
 */
  static async _fetch(endpoint, options = {}, errorMessage, retryCount = 0) {
    const MAX_RETRIES = 3;

    // Enforce HTTPS on every request
    this._enforceHTTPS();

    const commonHeaders = this._getCommonHeaders();

    // Merge common headers with any specific headers provided in options
    const mergedOptions = {
      ...options,
      headers: {
        ...commonHeaders,
        ...(options.headers || {})
      }
    };

    let response;
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, mergedOptions);
    } catch (networkError) {
      // True network error (no internet, CORS, DNS failure, etc.)
      // Retry on network errors with exponential backoff
      if (retryCount < MAX_RETRIES) {
        console.warn(`Network error, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await this._exponentialBackoff(retryCount);
        return this._fetch(endpoint, options, errorMessage, retryCount + 1);
      }

      console.error('Network error:', networkError);
      const error = new Error('Unable to reach the server. Please check your connection.');
      error.originalError = networkError;
      error.isNetworkError = true;
      throw error;
    }

    // Handle 429 Rate Limit - retry with exponential backoff
    if (response.status === 429) {
      if (retryCount < MAX_RETRIES) {
        // Check for Retry-After header
        const retryAfter = response.headers.get('Retry-After');
        const delayMs = retryAfter ? parseInt(retryAfter) * 1000 : null;

        if (delayMs) {
          console.warn(`Rate limited. Retrying after ${delayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          console.warn(`Rate limited. Retrying with exponential backoff (${retryCount + 1}/${MAX_RETRIES})...`);
          await this._exponentialBackoff(retryCount);
        }

        return this._fetch(endpoint, options, errorMessage, retryCount + 1);
      }

      // Max retries exceeded
      const error = new Error('Too many requests. Please try again later.');
      error.status = 429;
      error.isRateLimitError = true;
      throw error;
    }

    // Handle 503 Service Unavailable - retry with exponential backoff
    if (response.status === 503) {
      if (retryCount < MAX_RETRIES) {
        console.warn(`Service unavailable. Retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await this._exponentialBackoff(retryCount);
        return this._fetch(endpoint, options, errorMessage, retryCount + 1);
      }

      const error = new Error('Service temporarily unavailable. Please try again later.');
      error.status = 503;
      error.isServiceUnavailableError = true;
      throw error;
    }

    // Handle 401 Unauthorized IMMEDIATELY - before parsing response
    if (response.status === 401) {
      // Clear all auth tokens using AuthService
      await AuthService.logout();

      // Redirect to login page
      if (this.navigationCallback) {
        this.navigationCallback('/login');
      } else {
        // Fallback: reload the page if navigation callback is not set
        window.location.href = '/login';
      }

      // Throw error to prevent further processing
      const error = new Error('Session expired. Redirecting to login...');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    // Parse the response body
    let responseData;
    try {
      responseData = await response.json();
    } catch (e) {
      // If JSON parsing fails, set responseData to null
      responseData = null;
    }

    // Now check if response was successful
    if (!response.ok) {
      // Create an error with the response data attached
      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.responseData = responseData;

      if (responseData != null && responseData.errorMessage != null) {
        error.message = responseData.errorMessage
      }
      throw error;
    }

    return responseData;
  }

  /**
   * Stores the authentication tokens and expiration times in local storage.
   * @param {object} response The successful login response body.
   */
  static _handleLoginResponse(response) {
    const {
      idToken,
      expiresIn, // Typically seconds until idToken expires
      refreshToken,
      refreshTokenExpiresIn,
      userId,
      isDebugger // isDebugger is a boolean flag
    } = response;

    // 1. Store the primary ID Token and its expiration
    if (idToken) {
      localStorage.setItem('authToken', idToken);
      // Store the expiration time (current time + expiresIn seconds)
      localStorage.setItem('authTokenExpiresAt', Date.now() + (expiresIn * 1000));
    }

    // 2. Store the Refresh Token for future renewal
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
      // Store the refresh token expiration time
      localStorage.setItem('refreshTokenExpiresAt', Date.now() + (refreshTokenExpiresIn * 1000));
    }

    // 3. Store User-specific data (optional but often useful)
    if (userId) {
      localStorage.setItem('userId', userId);
    }

    // 4. Store the isDebugger flag (optional)
    localStorage.setItem('isDebugger', isDebugger ? 'true' : 'false');

    console.log(`Login successful for user ${userId}. Tokens stored.`);
  }

  // ====================================================================

  /**
   * Logs in a user.
   * @param {string} userName The user's username.
   * @param {string} password The user's password.
   * @param {string} [refreshToken] The optional refresh token for token renewal.
   * @returns {Promise<any>} The response containing auth tokens.
   */
  static async login(userName, password, refreshToken) {
    const requestBody = {
      userName,
      password,
      ...(refreshToken && { refreshToken })
    };

    const response = await this._fetch(
      '/login',
      {
        // Headers are automatically handled in _fetch
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Login failed'
    );

    this._handleLoginResponse(response);

    return response;
  }

  static async signup(userName, email, password) {
    const requestBody = {
      userName,
      password,
      email,
    };

    const response = await this._fetch(
      '/signup',
      {
        // Headers are automatically handled in _fetch
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Signup failed'
    );

    return response;
  }

  static async googleLogin(googleAccessToken) {
    const response = await this._fetch(
      '/loginWithGoogle',
      {
        method: 'POST',
        body: JSON.stringify({ providerName: 'GOOGLE', accessToken: googleAccessToken }),
      },
      'Google login failed'
    );
    return response;
  }

  static async confirmSignup(userName, confirmationCode) {
    const requestBody = {
      userName,
      confirmationCode,
    };

    const response = await this._fetch(
      '/confirmSignup',
      {
        // Headers are automatically handled in _fetch
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Confirming signup failed'
    );

    return response;
  }

  static async forgotPassword(email) {
    const requestBody = {
      email,
    };

    const response = await this._fetch(
      '/forgotPassword',
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Operation failed'
    );

    return response;
  }

  static async confirmForgotPassword(email, verificationCode, password) {
    const requestBody = {
      email,
      verificationCode,
      password
    };

    const response = await this._fetch(
      '/confirmForgotPassword',
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Operation failed'
    );

    return response;
  }

  static async resendConfirmCode(userName) {
    const requestBody = {
      userName,
    };

    const response = await this._fetch(
      '/resendConfirmCode',
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Operation failed'
    );

    return response;
  }

  static async fetchLLMs() {
    return this._fetch(
      '/llms',
      { method: 'GET' }, // Default method is GET, but good practice to be explicit
      'Failed to fetch LLMs'
    );
  }

  static async getAccessKeys() {
    return CacheService.get(
      'accessKeys',
      () => this._fetch(
        '/listApiKeys',
        { method: 'GET' },
        'Failed to fetch access keys'
      ),
      { ttl: 5 * 60 * 1000 } // 5 minutes
    );
  }

  static async createAccessKey(keyType, name) {
    const result = await this._fetch(
      '/createApiKey',
      {
        method: 'POST',
        body: JSON.stringify({ keyType, name })
      },
      'Failed to create access key'
    );
    
    // Invalidate cache after mutation
    CacheService.invalidate('accessKeys');
    
    return result;
  }

  static async deleteAccessKey(apiKey) {
    const result = await this._fetch(
      '/deleteApiKey',
      {
        method: 'POST',
        body: JSON.stringify({ apiKey })
      },
      'Failed to delete access key'
    );
    
    // Invalidate cache after mutation
    CacheService.invalidate('accessKeys');
    
    return result;
  }

  static async updateApiKey(apiKey, isActive, allowedModelIds) {
    const result = await this._fetch(
      '/updateApiKey',
      {
        method: 'POST',
        body: JSON.stringify({ apiKey, isActive, allowedModelIds })
      },
      'Failed to update access key'
    );

    // Invalidate cache after mutation
    CacheService.invalidate('accessKeys');

    return result;
  }

  static async getMyLLMs() {
    return CacheService.get(
      'MyLLMs',
      () => this._fetch(
        '/listMyLLMs',
        { method: 'GET' },
        'Failed to fetch my LLMs'
      ),
      { ttl: 5 * 60 * 1000 } // 5 minutes
    );
  }

  static async getPublicLLMs() {
    return CacheService.get(
      'publicLLMs',
      () => this._fetch(
        '/listPublicLLMs',
        { method: 'GET' },
        'Failed to fetch public LLMs'
      ),
      { ttl: 5 * 60 * 1000 } // 5 minutes
    );
  }

  static async updateLLM(llmId, updateData) {
    const userId = localStorage.getItem('userId');

    const requestBody = {
      ownerId: userId,
      llmId: llmId,
      name: updateData.name,
      description: updateData.description,
      version: updateData.version,
      category: updateData.category,
      tags: updateData.tags,
      fileExt: updateData.fileExt,
      licenseUrl: updateData.licenseUrl,
      projectUrl: updateData.projectUrl,
      inputTypes: updateData.inputTypes,
      outputTypes: updateData.outputTypes,
      // Note: groupId is NOT sent - backend will auto-generate from groupName
      groupName: updateData.groupName
    };

    const result = await this._fetch(
      '/updateLLM',
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Failed to update LLM'
    );

    // Invalidate cache after mutation
    CacheService.invalidate('MyLLMs');
    CacheService.invalidate('publicLLMs');

    return result;
  }

  static async initApproveLLM(llmId, updateData) {
    const userId = localStorage.getItem('userId');

    const requestBody = {
      ownerId: userId,
      llmId: llmId,
    };

    const result = await this._fetch(
      '/initApproveLLM',
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      },
      'Failed to initialize LLM approval'
    );

    // Invalidate cache after mutation
    CacheService.invalidate('MyLLMs');

    return result;
  }

  // Group Management
  // Note: For Approach 1, updateLLM() handles group assignment via groupName field
  // These methods are optional for future use if you need dedicated group operations

  static async getDownloadsAnalytics(type = 'user', timeRange = 30, limit = 100) {
    const params = new URLSearchParams({
      type,
      timeRange,
      limit
    });

    return CacheService.get(
      'downloadAnalytics',
      () => this._fetch(
        `/downloadAnalytics?${params.toString()}`,
        { method: 'GET' },
        'Failed to fetch download analytics'
      ),
      { ttl: 5 * 60 * 1000, params: { type, timeRange, limit } } // 5 minutes
    );
  }

  static async getBillingInfo(type = 'billing', timeRange = 30) {
    const params = new URLSearchParams({
      type,
      timeRange
    });

    return CacheService.get(
      'billingInfo',
      () => this._fetch(
        `/downloadAnalytics?${params.toString()}`,
        { method: 'GET' },
        'Failed to fetch billing info'
      ),
      { ttl: 5 * 60 * 1000, params: { type, timeRange } } // 5 minutes
    );
  }

  static clearAnalyticsCache() {
    CacheService.invalidatePattern('downloadAnalytics');
    CacheService.invalidatePattern('billingInfo');
  }

  // Payment Methods (Stripe)
  static async getPaymentMethods() {
    return CacheService.get(
      'listPaymentMethods',
      () => this._fetch(
        '/listPaymentMethods',
        { method: 'GET' },
        'Failed to fetch payment methods'
      ),
      { ttl: 5 * 60 * 1000 } // 5 minutes
    );
  }

  static clearPaymentMethodsCache() {
    CacheService.invalidate('paymentMethods');
  }

  // Stripe Checkout Session
  static async createCheckoutSession() {
    const result = await this._fetch(
      '/createCheckoutSession',
      {
        method: 'POST',
        body: JSON.stringify({
        })
      },
      'Failed to create checkout session'
    );

    return result;
  }

  // Delete Payment Method
  static async deletePaymentMethod(paymentMethodId) {
    const result = await this._fetch(
      '/deletePaymentMethod',
      {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId })
      },
      'Failed to delete payment method'
    );

    // Invalidate cache after mutation
    CacheService.invalidate('listPaymentMethods');

    return result;
  }

  // Set Default Payment Method
  static async setDefaultPaymentMethod(paymentMethodId) {
    const result = await this._fetch(
      '/setDefaultPaymentMethod',
      {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId })
      },
      'Failed to set default payment method'
    );

    // Invalidate cache after mutation
    CacheService.invalidate('listPaymentMethods');

    return result;
  }

  // Submit Support Request
  static async submitSupportRequest({ title, details, category }) {
    return this._fetch(
      '/submitSupportRequest',
      {
        method: 'POST',
        body: JSON.stringify({ title, details, category })
      },
      'Failed to submit support request'
    );
  }

  // Account-level spending limits
  static async getSpendingLimits() {
    return CacheService.get(
      'spendingLimits',
      () => this._fetch(
        '/getSpendingLimits',
        { method: 'GET' },
        'Failed to fetch spending limits'
      ),
      { ttl: 5 * 60 * 1000 }
    );
  }

  static async updateSpendingLimits(config) {
    const result = await this._fetch(
      '/updateSpendingLimits',
      {
        method: 'POST',
        body: JSON.stringify(config)
      },
      'Failed to update spending limits'
    );

    CacheService.invalidate('spendingLimits');

    return result;
  }

  // Per-key spending limits
  static async getKeySpendingLimit(apiKey) {
    const params = new URLSearchParams({ apiKey });
    return CacheService.get(
      'keySpendingLimit',
      () => this._fetch(
        `/getKeySpendingLimit?${params.toString()}`,
        { method: 'GET' },
        'Failed to fetch key spending limit'
      ),
      { ttl: 5 * 60 * 1000, params: { apiKey } }
    );
  }

  static async updateKeySpendingLimit(apiKey, config) {
    const result = await this._fetch(
      '/updateKeySpendingLimit',
      {
        method: 'POST',
        body: JSON.stringify({ apiKey, ...config })
      },
      'Failed to update key spending limit'
    );

    CacheService.invalidatePattern('keySpendingLimit');

    return result;
  }

  static clearSpendingLimitsCache() {
    CacheService.invalidate('spendingLimits');
    CacheService.invalidatePattern('keySpendingLimit');
  }

  static async getUser() {
    return this._fetch('/user', { method: 'GET' }, 'Failed to fetch user');
  }

  // Game data
  static async getTests() {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    return CacheService.get(
      'gameTests',
      () => this._fetch('/getTests', { method: 'GET' }, 'Failed to fetch tests'),
      { ttl: 60 * 60 * 1000, params: { lang } }
    );
  }

  static async getDiagnoses() {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    return CacheService.get(
      'gameDiagnoses',
      () => this._fetch('/getDiagnoses', { method: 'GET' }, 'Failed to fetch diagnoses'),
      { ttl: 60 * 60 * 1000, params: { lang } }
    );
  }

  static async getLevels() {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    return CacheService.get(
      'gameLevels',
      () => this._fetch('/getLevels', { method: 'GET' }, 'Failed to fetch levels'),
      { ttl: 60 * 60 * 1000, params: { lang } }
    );
  }

  static async getBackgroundQuestions() {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    return CacheService.get(
      'backgroundQuestions',
      () => this._fetch('/getBackgroundQuestions', { method: 'GET' }, 'Failed to fetch background questions'),
      { ttl: 60 * 60 * 1000, params: { lang } }
    );
  }

  static async getPhysicalExam() {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    return CacheService.get(
      'physicalExam',
      () => this._fetch('/getPhysicalExam', { method: 'GET' }, 'Failed to fetch physical exam items'),
      { ttl: 60 * 60 * 1000, params: { lang } }
    );
  }

  static async getGameProgress() {
    return this._fetch('/getGameProgress', { method: 'GET' }, 'Failed to fetch game progress');
  }

  static async getTestResult(caseId, testId) {
    return this._fetch('/getTestResult', {
      method: 'POST',
      body: JSON.stringify({ caseId, testId }),
    }, 'Failed to get test result');
  }

  static async getBackgroundAnswer(caseId, questionId) {
    return this._fetch('/getBackgroundAnswer', {
      method: 'POST',
      body: JSON.stringify({ caseId, questionId }),
    }, 'Failed to get background answer');
  }

  static async getPhysicalExamAnswer(caseId, examItemId) {
    return this._fetch('/getPhysicalExamAnswer', {
      method: 'POST',
      body: JSON.stringify({ caseId, examItemId }),
    }, 'Failed to get physical exam answer');
  }

  static async getCaseAnswer(caseId) {
    return this._fetch(`/getCaseAnswer?caseId=${encodeURIComponent(caseId)}`, { method: 'GET' }, 'Failed to get case answer');
  }

  static async submitDiagnosis(caseId, diagnosis, testsOrdered, historyItemsAccessed, physicalExamAccessed) {
    return this._fetch('/submitDiagnosis', {
      method: 'POST',
      body: JSON.stringify({ caseId, diagnosis, testsOrdered, historyItemsAccessed, physicalExamAccessed }),
    }, 'Failed to submit diagnosis');
  }

  static async updateGameProgress(unlockedLevel, completedCases, wrongAttempts, wentBroke) {
    CacheService.invalidate('gameProgress');
    return this._fetch('/updateGameProgress', {
      method: 'POST',
      body: JSON.stringify({ unlockedLevel, completedCases, wrongAttempts, wentBroke }),
    }, 'Failed to update game progress');
  }
}

export default APIService;